import React, { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

// Client Admin pages
import ClientAdminLogin from '@pages/client-admin/Login'
import Dashboard from '@pages/client-admin/Dashboard'
import CMSDashboard from '@pages/client-admin/CMSDashboard'
import PageEditor from '@pages/client-admin/PageEditor'
import ProfessionalEventManagement from '@pages/client-admin/ProfessionalEventManagement'
import EventDetail from '@pages/client-admin/EventDetail'
import RosterManagement from '@pages/client-admin/RosterManagement'
import EventFixtures from '@pages/client-admin/EventFixtures'
import CertificateGeneration from '@pages/client-admin/CertificateGeneration'
import PlayerManagement from '@pages/client-admin/PlayerManagement'
import ProfessionalPlayerManagement from '@pages/client-admin/ProfessionalPlayerManagement'
import PlayerStateWiseView from '@pages/client-admin/PlayerStateWiseView'
import OfficialManagement from '@pages/client-admin/OfficialManagement'
import ProfessionalOfficialManagement from '@pages/client-admin/ProfessionalOfficialManagement'
import AddOfficial from '@pages/client-admin/AddOfficial'
import TournamentRegistrations from '@pages/client-admin/TournamentRegistrations'
import TournamentResults from '@pages/client-admin/TournamentResults'
import UserManagement from '@pages/client-admin/UserManagement'
import AddUser from '@pages/client-admin/AddUser'
import EditUser from '@pages/client-admin/EditUser'
import UserProfile from '@pages/client-admin/UserProfile'
import SiteSettings from '@pages/client-admin/SiteSettings'
import AdminNotifications from '@pages/client-admin/AdminNotifications'
import MemberEditRequests from '@pages/client-admin/MemberEditRequests'
import AdminStateTournamentRecords from '@pages/client-admin/AdminStateTournamentRecords'
import AdminRegistrationRequests from '@pages/client-admin/AdminRegistrationRequests'
import NotFoundPage from '@pages/common/NotFoundPage'
import LoaderTest from '@pages/test/LoaderTest'

// Components
import ClientAdminLayout from '@components/client-admin/ClientAdminLayout'
import ProtectedRoute from '@components/client-admin/ProtectedRoute'

// Hooks
import { useClientAdminAuth } from '@contexts/AuthContext'
import { useTenant } from '@contexts/TenantContext'

// Theme-specific admin layouts (lazy loaded)
const ThrowballAdminLayout = lazy(() => import('@components/themes/throwball/admin/ThrowballAdminLayout'))

// Theme-aware admin layout wrapper
const ThemeAwareAdminLayout = ({ children }) => {
  const { tenant, loading: tenantLoading } = useTenant()

  if (tenantLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f9fafb'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #db2777',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
      </div>
    )
  }

  const themeName = tenant?.theme || 'default'

  // Return theme-specific layout based on tenant theme
  if (themeName === 'throwball') {
    return (
      <Suspense fallback={
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #fce7f3',
            borderTop: '4px solid #db2777',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
        </div>
      }>
        <ThrowballAdminLayout>{children}</ThrowballAdminLayout>
      </Suspense>
    )
  }

  // Default layout
  return <ClientAdminLayout>{children}</ClientAdminLayout>
}

const ClientAdminRoutes = () => {
  const { isAuthenticated, loading } = useClientAdminAuth()

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f9fafb'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #022a5e',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }}>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public routes */}
      {/* Redirect /login to /user-login for tenant sites */}
      <Route path="/login" element={<Navigate to="/user-login" replace />} />
      
      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ThemeAwareAdminLayout>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                
                {/* Content Management */}
                <Route path="/cms" element={<CMSDashboard />} />
                <Route path="/pages/new" element={<PageEditor />} />
                <Route path="/pages/:id/edit" element={<PageEditor />} />
                
                {/* Event Management */}
                <Route path="/events" element={<ProfessionalEventManagement />} />
                <Route path="/events/past" element={<ProfessionalEventManagement filter="past" />} />
                <Route path="/events/upcoming" element={<ProfessionalEventManagement filter="upcoming" />} />
                <Route path="/events/:id" element={<EventDetail />} />
                <Route path="/events/:eventId/registrations" element={<TournamentRegistrations />} />
                <Route path="/events/:eventId/results" element={<TournamentResults />} />

                {/* Player Management */}
                <Route path="/players" element={<PlayerManagement />} />
                <Route path="/players-users" element={<ProfessionalPlayerManagement />} />
                <Route path="/players-state-wise" element={<PlayerStateWiseView />} />

                {/* Official Management */}
                <Route path="/officials" element={<OfficialManagement />} />
                <Route path="/officials/add" element={<AddOfficial />} />
                <Route path="/officials-old" element={<ProfessionalOfficialManagement />} />
                
                {/* Roster Management */}
                <Route path="/events/:eventId/fixtures" element={<EventFixtures />} />
                <Route path="/rosters" element={<RosterManagement />} />

                {/* Certificate Generation */}
                <Route path="/certificates" element={<CertificateGeneration />} />

                {/* User Management */}
                <Route path="/users" element={<UserManagement />} />
                <Route path="/users/add" element={<AddUser />} />
                <Route path="/users/edit/:id" element={<EditUser />} />
                <Route path="/users/:id" element={<UserProfile />} />

                {/* Notifications */}
                <Route path="/notifications" element={<AdminNotifications />} />

                {/* Member Edit Requests */}
                <Route path="/member-edit-requests" element={<MemberEditRequests />} />

                {/* State Tournament Records */}
                <Route path="/state-tournament-records" element={<AdminStateTournamentRecords />} />
                <Route path="/registration-requests" element={<AdminRegistrationRequests />} />

                {/* Settings */}
                <Route path="/settings" element={<SiteSettings />} />

                {/* Test Pages */}
                <Route path="/test-loader" element={<LoaderTest />} />

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </ThemeAwareAdminLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

export default ClientAdminRoutes